export interface ConversationRecipient {
  id?: number | string | null;
  name?: string | null;
  email?: string | null;
}

/** How many names the header prints before it starts counting. */
export const RECIPIENT_INLINE_LIMIT = 3;

/**
 * What identifies one person across a recipient list.
 *
 * The address first, because that is what the backend matched on: cc and bcc
 * reach a row as `User.where(email: cc)`, a lookup with no `distinct`, so an
 * account holding more than one user record for an address — someone
 * deactivated and re-invited, or the same person in two orgs — returns that
 * person once per record and the header printed their name twice. Two user
 * records can share a name; they can never share an address.
 */
const identityOf = (recipient: ConversationRecipient, index: number) => {
  const email = recipient?.email?.trim().toLowerCase();
  if (email) return `email:${email}`;
  if (recipient?.id !== undefined && recipient?.id !== null) {
    return `id:${recipient.id}`;
  }
  const name = recipient?.name?.trim().toLowerCase();
  // No address, no id and no name is not a person we can merge on — keep it, so
  // the count still reflects how many the email actually went to.
  return name ? `name:${name}` : `index:${index}`;
};

/** The same people, each listed once, in the order the backend sent them. */
export const dedupeRecipients = (
  recipients: ConversationRecipient[] = []
): ConversationRecipient[] => {
  const seen = new Set<string>();

  return (recipients ?? []).filter((recipient, index) => {
    if (!recipient) return false;
    const identity = identityOf(recipient, index);
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

/** A recipient's display name, falling back to the address we do have. */
export const recipientLabel = (recipient: ConversationRecipient): string =>
  recipient?.name?.trim() || recipient?.email?.trim() || '';

export interface RecipientSummary {
  /** Deduped, in order. */
  all: ConversationRecipient[];
  /** The ones printed inline. */
  shown: ConversationRecipient[];
  /** The rest — named in the tooltip, counted in the trigger. */
  hidden: ConversationRecipient[];
}

/**
 * Splits a recipient list into the part the header prints and the part it
 * counts.
 *
 * Fourteen names set as prose wrapped to four lines above a two-line email, so
 * the row read as a list of people rather than as a message. Bounded here rather
 * than truncated in CSS, because the count has to be honest about how many are
 * not shown.
 */
export const summarizeRecipients = (
  recipients: ConversationRecipient[] = [],
  limit: number = RECIPIENT_INLINE_LIMIT
): RecipientSummary => {
  const all = dedupeRecipients(recipients);

  return {
    all,
    shown: all.slice(0, limit),
    hidden: all.slice(limit),
  };
};
